export interface WindowExtend extends Window {
  evolu: Evolu<Database>;
  Video: any;
}

declare global {
  var evolu: Evolu<Database>;
  var Video: any;
  var window: WindowExtend;
}

import uuid from 'react-native-uuid';
import * as S from '@effect/schema/Schema';

import {createEvolu, database} from '@evolu/react-native';

import {ItemTable} from './tables/item';
import {
  Auction,
  Contact,
  DatabaseItem,
  Device,
  Folder,
  FolderType,
  Media,
  UserInfo,
} from '../../types';

import {Platform} from 'react-native';
import {QueryClient} from 'react-query';
import {Evolu, EvoluConfig} from '@evolu/common';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  },
});

const Database = database({
  item: ItemTable,
});

export type Database = S.Schema.Type<typeof Database>;

const dbConns: {[key: string]: Evolu<Database>} = {};

export const dbMnemoCons: {[key: string]: Evolu<Database>} = {};

export let defaultDBName = 'starpy2';

export let dbSocket: WebSocket | undefined;
export const createDBConn = (
  dbname = `${defaultDBName}`,
  mnemonic?: string,
  index?: boolean,
): Evolu<Database> | Promise<Evolu<Database>> => {
  if (dbConns[dbname]) return dbConns[dbname];
  if (mnemonic && dbMnemoCons[mnemonic]) return dbMnemoCons[mnemonic];

  const ops: Partial<EvoluConfig<Database>> = {
    name: dbname,
    syncUrl: 'https://db.starpy.me',
  };
  if (Platform.OS == 'web') {
    ops.enableWebsocketConnection = true;
  } else {
    if (dbname === defaultDBName)
      ops.enableWebsocketConnection = true;
    else ops.externalWebsocketConnection = dbSocket;
  }
  if (mnemonic) ops.mnemonic = mnemonic as any;
  const db = createEvolu(Database, ops);
  if (dbname === defaultDBName)
    db.socket.then(socket => {
      dbSocket = socket;
      console.info('dbSocket', socket);
    });
  dbConns[dbname] = db;
  if (mnemonic) dbMnemoCons[mnemonic] = db;
  if (index === true) {
    return new Promise(res => {
      indexAllFoldersForDB(db).then(() => res(db));

      db.subscribeSyncState(async () => {
        const state = db.getSyncState();
        if (state._tag === 'SyncStateIsSynced') {
          if (!isFirstSync[dbname]) {
            isFirstSync[dbname] = true;
          }
          // console.info('state', state, dbname);
          queryClient.refetchQueries('folderContent_' + dbname);
          indexAllFoldersForDB(db);
        }
      });
    });
  }
  return db;
};
const isFirstSync: {[key: string]: boolean} = {};

export const indexAllFoldersForDB = async (e: Evolu<Database>) => {
  try {
    const query = e?.createQuery(db =>
      db
        .selectFrom('item')
        .selectAll()
        .where('mnemonic', 'is not', null)
        .where('isDeleted', 'is', null),
    );
    const folders = await e?.loadQuery(query);
    // console.info('folders', folders.rows);
    const folderPromises = folders.rows
      .map(async nitem => {
        const item = {...nitem, ...nitem.item};
        if (item.isDeleted) return;
        if (!isFirstSync[item.id]) {
          isFirstSync[item.id] = true;
          dbConns[item.id] = e;
        }
        if (item.isAuction) {
          queryClient.refetchQueries('auction_' + item.id);
        }
        if (item.isEditorProject) {
          queryClient.refetchQueries(['projectLayers', item.id]);
          queryClient.refetchQueries(item.id + '_comments');
        }
        queryClient.refetchQueries('folderContent_' + item.id);
      })
      .filter(i => i);
    await Promise.all(folderPromises);
  } catch (err) {
    console.error(err);
  }
};

export const getUserData = async (): Promise<UserInfo> => {
  const authData = {};
  const [userData] = await findBy('userDoc', true);
  const devices = await findBy('isDevice', true);
  return {
    ...userData,
    ...authData,
    devices,
  };
};

export const getFolderContent = async (
  folderId: string,
  mnemonic?: string,
) => {
  let e = evolu;
  if (mnemonic) {
    e = await createDBConn(folderId, mnemonic, true);
  } else {
    const folder = (await getById(folderId)) as Folder;
    if (folder && folder.mnemonic) {
      e = await createDBConn(folderId, folder.mnemonic, true);
    }
  }
  const query = e?.createQuery(db =>
    db
      .selectFrom('item')
      .selectAll()
      .where('folderId', '=', folderId)
      .where('isDeleted', 'is', null)
      .orderBy('orderId', 'asc')
      .orderBy('lastUpdated', 'desc'),
  );
  const data = await e?.loadQuery(query);
  return data.rows.map(i => i.item as Folder | Media | Contact);
};

export const getPublicFolderContent = async (folderId: string) =>
  getFolderContent(folderId);

export const getFoldersByType = async (
  type: FolderType,
  mnemonic?: string,
): Promise<Folder[]> => {
  const e =
    mnemonic !== undefined
      ? await createDBConn(mnemonic, mnemonic)
      : evolu;
  const query = e.createQuery(db =>
    db
      .selectFrom('item')
      .selectAll()
      .where('folderType', '=', type)
      .where('folderId', 'is', null)
      .where('isDeleted', 'is', null)
      .orderBy('orderId', 'asc')
      .orderBy('lastUpdated', 'desc'),
  );
  const data = await e?.loadQuery(query);
  console.info('folders', data.rows, mnemonic);
  return data.rows.map(i => i.item as Folder);
};

export const writeItem = async (data: any, e?: Evolu<Database>) => {
  const parentFolderId =
    Platform.OS === 'web'
      ? new URL(window?.location?.toString()).searchParams.get(
          'parentFolderId',
        )
      : '';
  let createdNewDbConn = false;
  let orderId = data.orderId || 1;
  if (data.mnemonic && data.isEditorComment) {
    e = await createDBConn(data.folderId, data.mnemonic);
    orderId =
      data.orderId ||
      (await getFolderContent(data.folderId, data.mnemonic)).length;
  } else {
    if (data.folderId || parentFolderId) {
      const folder: Folder = await getById(
        data.folderId || parentFolderId,
      );
      if (folder && folder.mnemonic) {
        e = await createDBConn(data.folderId, folder.mnemonic);
        if (
          data.isFolder ||
          data.isEditorLayer ||
          data.isEditorProject
        ) {
          data.mnemonic = folder.mnemonic;
          dbConns[data.id] = e;
          createdNewDbConn = true;
        }
        if (data.isMediaNew || data.isEncoderProject) {
          data.extractor = 'wt';
        }
      }
      orderId =
        data.orderId ||
        (await getFolderContent(data.folderId)).length;
    }
  }
  data.orderId = orderId;

  const item = await (e || evolu).createOrUpdate('item', {
    id: data.id,
    title: data.title,
    name: data.name,
    item: data,
    isFolder: data.isFolder,
    isContact: data.isContact,
    isMediaNew: data.isMediaNew,
    isDevice: data.isDevice,
    userDoc: data.userDoc,
    folderId: data.folderId,
    folderType: data.folderType,
    chatId: data.chatId,
    createdAt: data.createdAt,
    isDeleted: data.isDeleted,
    isEditorProject: data.isDeleted,
    isEditorLayer: data.isDeleted,
    orderId,
    mediaId: data.mediaId,
    auctionId: data.auctionId,
    mnemonic: data.mnemonic,
    lastUpdated: new Date().valueOf(),
  });
  if (data.mnemonic && data.isFolder && !e) {
    console.info('create a new index for a new folder');
    e = await createDBConn(data.id, data.mnemonic, true);
  }
  return item;
};

export const findByOriginalId = async (id: string) =>
  findBy('originalId', id);

export const findBy = async (
  key: string,
  value: string | boolean | number,
) => {
  const query = evolu?.createQuery(db =>
    db.selectFrom('item').selectAll().where(key, '=', value),
  );
  const data = await evolu?.loadQuery(query);
  // console.info('findBy', data);
  return data.rows.map(i => i.item);
};

export const getById = async <T>(
  id: string,
  folderId?: string,
): Promise<Media | Folder | Contact | Device | Auction | T> => {
  let e = evolu;
  if (folderId) {
    e = dbConns[folderId];
    if (!e && id !== folderId) {
      const folder = (await getById(folderId)) as Folder;
      if (folder?.mnemonic) {
        e = await createDBConn(folder.id, folder.mnemonic);
      } else {
        e = evolu;
      }
    }
  }
  const query = e?.createQuery(db =>
    db.selectFrom('item').selectAll().where('id', '=', id),
  );
  const item = await e?.loadQuery(query);
  let i = item?.row?.item;
  if (i?.isFolder || i?.isEditorLayer || i?.isEditorProject) {
    if (!dbConns[i.id] && i.mnemonic) {
      dbConns[i] = e;
    }
  }
  if (!i && id !== '' && id !== undefined && id !== null) {
    if (dbConns[id] && id !== folderId) return await getById(id, id);
  }
  if (!i) {
    const max = await Promise.all(
      Object.keys(dbConns).map(
        async dbConnId => await dbConns[dbConnId].loadQuery(query),
      ),
    );
    const fmx = max.filter(m => m.rows.length).flat();
    console.info('i', max, fmx);
    if (fmx.length) i = fmx[0].row.item;
  }
  return i;
};

export const initDatabaseRecord = async (
  dbname: string,
  mnemonic: string,
  e?: Evolu,
) => {
  let databases;
  if (!e) {
    console.info(
      'checking for database record for initial db ',
      defaultDBName,
    );
    databases = await getFoldersByType(FolderType.database);
    if (databases.length) {
      const exists = databases.find(i => i.mnemonic === mnemonic);
      if (exists) return exists;
    }
  }
  const id = uuid.v4().toString();
  const ndb: DatabaseItem = {
    id,
    title: `${dbname}_${id}`,
    mnemonic,
    isDatabase: true,
    folderType: FolderType.database,
    date: new Date().valueOf(),
    added: new Date().valueOf().toString(),
    orderId: databases?.length + 1 || 2,
    userId: 'super',
  };
  return await writeItem(ndb, e);
};

const evolu = createDBConn();

export {evolu};
